import type { Order, User } from '../types/index.js'
import type { DailyTimesheet, TimesheetHistoryItem } from '../contracts/timesheet.js'
import type { ApiClient } from '../types/api.js'
import type { 
  CreateOrderRequest, 
  UpdateOrderRequest,
  OrderResponse,
  OrderQuery,
  LoginResponse,
  WorkerSearchResponse,
  WorkerCreateResponse
} from '../contracts/index.js'

// ✅ UNCLE BOB: No environment detection - always use real database-backed API
console.log('🏗️ Clean Architecture: Using database-backed API in all environments')

// Dynamic import wrapper for .js compatibility in serverless functions
let contractsPromise: Promise<typeof import('../contracts/index.js')> | null = null
const getContracts = async () => {
  if (!contractsPromise) {
    contractsPromise = import('../contracts/index.js')
  }
  return contractsPromise
}

// Utility function to handle API error responses safely
const handleApiError = async (response: Response): Promise<never> => {
  let errorMessage = `HTTP ${response.status}: ${response.statusText}`
  
  // Check content type to decide how to parse the response
  const contentType = response.headers.get('content-type') || ''
  
  try {
    if (contentType.includes('application/json')) {
      const errorData = await response.json()
      errorMessage = errorData.error || errorData.message || errorMessage
    } else {
      const textData = await response.text()
      if (textData) {
        errorMessage = textData
      }
    }
  } catch (parseError) {
    // If we can't parse the error response, use the default message
    console.warn('Could not parse error response:', parseError)
  }
  
  const error = new Error(errorMessage) as Error & { statusCode?: number }
  error.statusCode = response.status
  throw error
}

// ✅ UNCLE BOB: Single API client implementation for all environments
const createApiClient = (): ApiClient => {
  return {
    async login(username: string, password: string): Promise<LoginResponse> {
      // Load contracts dynamically for .js compatibility
      const { LoginRequestSchema, LoginResponseSchema, debugValidation } = await getContracts()
      
      // Validate request data using our contract
      const requestData = { username, password }
      const validatedRequest = debugValidation(LoginRequestSchema, requestData, 'login request')
      if (!validatedRequest) {
        throw new Error('Invalid login credentials format')
      }

      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(validatedRequest)
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // Validate response using our contract
      const validatedResponse = debugValidation(LoginResponseSchema, result, 'login response')
      if (!validatedResponse) {
        throw new Error('Invalid response format from login API')
      }
      
      return validatedResponse
    },

    async logout(): Promise<{ success: boolean; message?: string }> {
      const response = await fetch('/api/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    async getOrders(token: string): Promise<Order[]> {
      const response = await fetch('/api/orders', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    async createOrder(token: string, orderData: { title: string; amount: number }): Promise<Order> {
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(orderData)
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    // Modern Orders API - Unified functionality using contract types
    async getOrdersWithFilters(token: string, filters?: { status?: string; jobNumber?: string; projectId?: string }): Promise<OrderResponse[]> {
      // Load contracts dynamically for .js compatibility
      const { OrderQuerySchema, OrderResponseSchema, debugValidation } = await getContracts()
      
      // Convert and validate filters using our contract
      const queryParams: OrderQuery = {
        status: filters?.status as 'pending' | 'active' | 'completed' | 'cancelled' | undefined,
        job_number: filters?.jobNumber,
        project_id: filters?.projectId
      }
      
      const validatedQuery = debugValidation(OrderQuerySchema, queryParams, 'getOrdersWithFilters query')
      if (!validatedQuery) {
        throw new Error('Invalid query parameters provided')
      }

      const params = new URLSearchParams()
      if (validatedQuery.status) params.append('status', validatedQuery.status)
      if (validatedQuery.job_number) params.append('job_number', validatedQuery.job_number)
      if (validatedQuery.project_id) params.append('project_id', validatedQuery.project_id)
      
      const response = await fetch(`/api/orders?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // Validate response array
      if (Array.isArray(result)) {
        return result.map((order, index) => 
          debugValidation(OrderResponseSchema, order, `getOrdersWithFilters response[${index}]`) || order
        )
      }
      
      return result
    },

    async createOrderWithItems(token: string, orderData: CreateOrderRequest): Promise<OrderResponse> {
      // Load contracts dynamically for .js compatibility
      const { CreateOrderRequestSchema, OrderResponseSchema, debugValidation } = await getContracts()
      
      // Validate request data using our contract
      const validatedData = debugValidation(CreateOrderRequestSchema, orderData, 'createOrderWithItems request')
      if (!validatedData) {
        throw new Error('Invalid order data provided')
      }
      
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(validatedData)
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // Validate response using contract
      const validatedResponse = debugValidation(OrderResponseSchema, result, 'createOrderWithItems response')
      if (!validatedResponse) {
        throw new Error('Invalid response format from create order API')
      }
      
      return validatedResponse
    },

    async updateOrder(token: string, orderId: string, updates: UpdateOrderRequest): Promise<OrderResponse> {
      // Load contracts dynamically for .js compatibility
      const { UpdateOrderRequestSchema, OrderResponseSchema, debugValidation } = await getContracts()
      
      // Validate request data using our contract
      const validatedUpdates = debugValidation(UpdateOrderRequestSchema, updates, 'updateOrder request')
      if (!validatedUpdates) {
        throw new Error('Invalid update data provided')
      }
      
      const response = await fetch(`/api/orders?id=${orderId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(validatedUpdates)
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // Extract the order from the response (API returns { order: OrderResponse })
      const order = result.order || result
      
      // Validate response using contract
      const validatedResponse = debugValidation(OrderResponseSchema, order, 'updateOrder response')
      if (!validatedResponse) {
        throw new Error('Invalid response format from update order API')
      }
      
      return validatedResponse
    },

    async deleteOrder(token: string, orderId: string): Promise<{ message: string; id: number }> {
      const response = await fetch(`/api/orders?id=${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    // Legacy aliases for backward compatibility (deprecated)
    async getOrdersEnhanced(token: string, filters?: { status?: string; jobNumber?: string }) {
      // Load contracts dynamically for .js compatibility
      const { OrderQuerySchema, OrderResponseSchema, debugValidation } = await getContracts()
      
      // Convert and validate filters using our contract
      const queryParams: OrderQuery = {
        status: filters?.status as 'pending' | 'active' | 'completed' | 'cancelled' | undefined,
        job_number: filters?.jobNumber
      }
      
      const validatedQuery = debugValidation(OrderQuerySchema, queryParams, 'getOrdersEnhanced query')
      if (!validatedQuery) {
        throw new Error('Invalid query parameters provided')
      }

      const params = new URLSearchParams()
      if (validatedQuery.status) params.append('status', validatedQuery.status)
      if (validatedQuery.job_number) params.append('job_number', validatedQuery.job_number)
      
      const response = await fetch(`/api/orders?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // Validate response array - each item should match OrderResponse schema
      if (Array.isArray(result)) {
        return result.map((order, index) => 
          debugValidation(OrderResponseSchema, order, `getOrdersEnhanced response[${index}]`) || order
        )
      }
      
      return result
    },

    async createOrderEnhanced(token: string, orderData: CreateOrderRequest): Promise<OrderResponse> {
      return this.createOrderWithItems(token, orderData)
    },

    async updateOrderEnhanced(token: string, orderId: string, updates: UpdateOrderRequest): Promise<OrderResponse> {
      return this.updateOrder(token, orderId, updates)
    },

    // Timesheet API - Database-backed implementation
    async getTimesheetToday(token: string): Promise<DailyTimesheet> {
      const response = await fetch('/api/timesheet-today', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // TODO: Add timesheet response validation once we have timesheet contracts
      // For now, return the response as-is since it matches the DailyTimesheet type
      return result
    },

    async getTimesheetHistory(token: string, days?: number): Promise<{ timesheets: TimesheetHistoryItem[]; total: number }> {
      const params = new URLSearchParams()
      if (days !== undefined) params.append('days', days.toString())
      
      const response = await fetch(`/api/timesheet-history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    async getTimesheetByDate(token: string, date: string): Promise<DailyTimesheet> {
      const response = await fetch(`/api/timesheet-by-date?date=${encodeURIComponent(date)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    async submitTimesheet(token: string, timesheet: Partial<DailyTimesheet>): Promise<DailyTimesheet> {
      const response = await fetch('/api/timesheet-submit', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(timesheet)
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // TODO: Add timesheet response validation once we have timesheet contracts
      return result
    },

    async saveDraft(token: string, timesheet: Partial<DailyTimesheet>): Promise<DailyTimesheet> {
      const response = await fetch('/api/timesheet-draft-save', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(timesheet)
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // TODO: Add timesheet response validation once we have timesheet contracts
      return result
    },

    // Workers API
    async searchWorkers(token: string, query: string): Promise<WorkerSearchResponse> {
      // Load contracts dynamically for .js compatibility
      const { WorkerSearchResponseSchema, debugValidation } = await getContracts()
      
      const params = new URLSearchParams()
      if (query) params.append('q', query)
      
      const response = await fetch(`/api/workers-search?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // Validate response using contract
      const validatedResponse = debugValidation(WorkerSearchResponseSchema, result, 'searchWorkers response')
      if (!validatedResponse) {
        throw new Error('Invalid response format from workers search API')
      }
      
      return validatedResponse
    },

    async createWorker(token: string, workerData: { name: string }): Promise<WorkerCreateResponse> {
      // Load contracts dynamically for .js compatibility
      const { WorkerCreateResponseSchema, debugValidation } = await getContracts()
      
      const response = await fetch('/api/workers-create', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(workerData)
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      const result = await response.json()
      
      // Validate response using contract
      const validatedResponse = debugValidation(WorkerCreateResponseSchema, result, 'createWorker response')
      if (!validatedResponse) {
        throw new Error('Invalid response format from create worker API')
      }
      
      return validatedResponse
    },

    // Export API
    async exportOrderPDF(token: string, orderId: number): Promise<OrderResponse> {
      const response = await fetch(`/api/orders/${orderId}/export/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    // Timesheet Reports methods
    async getTimesheetReports(token: string, filters?: {
      startDate?: string
      endDate?: string
      employee?: string
      status?: 'submitted' | 'approved' | 'rejected'
      limit?: number
      offset?: number
    }) {
      const params = new URLSearchParams()
      if (filters?.startDate) params.append('startDate', filters.startDate)
      if (filters?.endDate) params.append('endDate', filters.endDate)
      if (filters?.employee) params.append('employee', filters.employee)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.limit) params.append('limit', filters.limit.toString())
      if (filters?.offset) params.append('offset', filters.offset.toString())
      
      const response = await fetch(`/api/reports/timesheets?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    },

    async approveTimesheet(token: string, submissionId: string, action: 'approve' | 'reject', notes?: string) {
      const response = await fetch('/api/reports/timesheets/approve', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ submissionId, action, notes })
      })
      
      if (!response.ok) {
        await handleApiError(response)
      }
      
      return response.json()
    }
  }
}

// ✅ UNCLE BOB: Single API client instance for all environments
const apiClient = createApiClient()

// Export API functions that use the single client
export const login = async (username: string, password: string): Promise<LoginResponse> => {
  return apiClient.login(username, password)
}

export const logout = async (): Promise<{ success: boolean; message?: string }> => {
  return apiClient.logout()
}

export const getOrders = async (token: string): Promise<Order[]> => {
  return apiClient.getOrders(token)
}

export const createOrder = async (token: string, orderData: { title: string; amount: number }): Promise<Order> => {
  return apiClient.createOrder(token, orderData)
}

// Modern Orders API
export const getOrdersWithFilters = async (token: string, filters?: { status?: string; jobNumber?: string; projectId?: string }) => {
  return apiClient.getOrdersWithFilters(token, filters)
}

export const createOrderWithItems = async (token: string, orderData: CreateOrderRequest) => {
  return apiClient.createOrderWithItems(token, orderData)
}

export const updateOrder = async (token: string, orderId: string, updates: UpdateOrderRequest) => {
  return apiClient.updateOrder(token, orderId, updates)
}

export const deleteOrder = async (token: string, orderId: string) => {
  return apiClient.deleteOrder(token, orderId)
}

// Legacy aliases for backward compatibility (deprecated)
/** @deprecated Use getOrdersWithFilters instead */
export const getOrdersEnhanced = async (token: string, filters?: { status?: string; jobNumber?: string }) => {
  return apiClient.getOrdersEnhanced(token, filters)
}

/** @deprecated Use createOrderWithItems instead */
export const createOrderEnhanced = async (token: string, orderData: CreateOrderRequest) => {
  return apiClient.createOrderEnhanced(token, orderData)
}

/** @deprecated Use updateOrder instead */
export const updateOrderEnhanced = async (token: string, orderId: string, updates: UpdateOrderRequest) => {
  return apiClient.updateOrderEnhanced(token, orderId, updates)
}

// Timesheet convenience functions
export const getTimesheetToday = async (token: string): Promise<DailyTimesheet> => {
  return apiClient.getTimesheetToday(token)
}

export const getTimesheetHistory = async (token: string, days?: number): Promise<{ timesheets: TimesheetHistoryItem[]; total: number }> => {
  return apiClient.getTimesheetHistory(token, days)
}

export const getTimesheetByDate = async (token: string, date: string): Promise<DailyTimesheet> => {
  return apiClient.getTimesheetByDate(token, date)
}

export const submitTimesheet = async (token: string, timesheet: Partial<DailyTimesheet>): Promise<DailyTimesheet> => {
  return apiClient.submitTimesheet(token, timesheet)
}

export const saveDraft = async (token: string, timesheet: Partial<DailyTimesheet>): Promise<DailyTimesheet> => {
  return apiClient.saveDraft(token, timesheet)
}

// Workers convenience functions
export const searchWorkers = async (token: string, query: string) => {
  return apiClient.searchWorkers(token, query)
}

export const createWorker = async (token: string, workerData: { name: string }) => {
  return apiClient.createWorker(token, workerData)
}

// Export order as PDF
export const exportOrderPDF = async (token: string, orderId: number) => {
  return apiClient.exportOrderPDF(token, orderId)
}

// Timesheet Reports API
export const getTimesheetReports = async (token: string, filters?: {
  startDate?: string
  endDate?: string
  employee?: string
  status?: 'submitted' | 'approved' | 'rejected'
  limit?: number
  offset?: number
}) => {
  return apiClient.getTimesheetReports(token, filters)
}

export const approveTimesheet = async (token: string, submissionId: string, action: 'approve' | 'reject', notes?: string) => {
  return apiClient.approveTimesheet(token, submissionId, action, notes)
}

// Export the singleton client for direct use if needed
export { apiClient }

// Export createApiClient for direct use if needed
export { createApiClient }

// Token and user management utilities
export const getToken = (): string | null => localStorage.getItem('token')
export const setToken = (token: string): void => localStorage.setItem('token', token)
export const clearToken = (): void => localStorage.removeItem('token')
export const getUser = (): Pick<User, 'id' | 'username' | 'role'> | null => {
  const user = localStorage.getItem('user')
  return user ? JSON.parse(user) : null
}
export const setUser = (user: Pick<User, 'id' | 'username' | 'role'>): void => localStorage.setItem('user', JSON.stringify(user))

// Development utilities for testing only
export const DevUtilities = {
  // ✅ UNCLE BOB: Utilities exist but don't change runtime behavior
  resetTestData: async (token: string): Promise<void> => {
    const response = await fetch('/api/test-reset', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    if (!response.ok) {
      await handleApiError(response)
    }
  },
  
  getCurrentApiMode: (): string => {
    return 'database-backed'
  }
}